import { type FunctionComponent, type ReactElement, useState } from 'react';
import { type ProjectFieldsFragment } from '../../../__generated__/graphql-client-types';
import { useAddProductToProjects } from '../../../hooks/save-to-project/add-product-to-projects.hooks';
import { type ProjectInputWithName } from '../../../types/project.types';
import { StyledButton, type StyledButtonProps } from '../../buttons';
import { ModalTrigger } from '../../common-components/modal/modal.component';
import { LoadableSaveToProject } from '../../project-components/loadable-save-to-project.component';

export type SaveToProjectButtonProps = {
	quantities?: number[];
	disabled?: boolean;
	wrapperClass?: string;
	buttonTextClassName?: string;
	triggerElement?: ReactElement;
	hasTriggerElement?: boolean;
	showQuantityInput?: boolean;
	showProjectsOnly?: boolean;
	onSaveToProject?: (projectInput: ProjectInputWithName[]) => void;
	onAttachOrderToProject?: (project?: ProjectFieldsFragment) => void;
} & Pick<StyledButtonProps, 'size'>;

export const SaveToProjectButton: FunctionComponent<SaveToProjectButtonProps> = ({
	disabled,
	quantities,
	size = 'DEFAULT',
	wrapperClass,
	buttonTextClassName = 'nowrap',
	triggerElement,
	hasTriggerElement = false,
	showQuantityInput = false,
	showProjectsOnly = false,
	onSaveToProject,
	onAttachOrderToProject
}) => {
	const { loadingAddToProjects } = useAddProductToProjects();
	const [isOpen, setIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isTriggerDisplayed, setIsTriggerDisplayed] = useState(triggerElement === undefined);

	const handleOpenModal = () => {
		setIsOpen(true);
	};

	const handleClose = () => {
		if (hasTriggerElement) {
			setIsTriggerDisplayed(false);
		} else {
			setIsLoading(false);
			setIsOpen(false);
		}
	};

	const handleSaveToProject = (projectInput: ProjectInputWithName[]) => {
		onSaveToProject?.(projectInput);
		handleClose();
	};

	const handleAttachOrderToProject = (project?: ProjectFieldsFragment) => {
		onAttachOrderToProject?.(project);
		handleClose();
	};

	return (
		<div className={`relative dib ${wrapperClass}`}>
			{hasTriggerElement && triggerElement ? (
				<ModalTrigger
					ariaLabel="save to project drawer trigger"
					className="dib w-inherit"
					openModal={() => setIsTriggerDisplayed(true)}
					triggerElement={triggerElement}
				/>
			) : (
				<StyledButton
					ariaLabel="save to project button"
					size={size}
					buttonStyle="SECONDARY"
					disabled={disabled || isLoading}
					isBusy={isLoading}
					busyText="Saving..."
					onClick={handleOpenModal}
					automationHook="save-to-project">
					<span className={`${buttonTextClassName} font-medium`} data-testid="buttonWithoutRecentlyUsedProject">
						Save to Project
					</span>
				</StyledButton>
			)}
			{(isOpen || (hasTriggerElement && isTriggerDisplayed)) && (
				<LoadableSaveToProject
					onClose={handleClose}
					handleAction={handleSaveToProject}
					handleAttachOrderToProject={handleAttachOrderToProject}
					isActionLoading={loadingAddToProjects}
					headerText="Save To Project"
					buttonText="Save"
					initialQuantity={quantities?.[0] || 1}
					showQuantityInput={showQuantityInput}
					showProjectsOnly={showProjectsOnly}
				/>
			)}
		</div>
	);
};
